// DashboardActivityItem.tsx - COMPLETE VERSION with ALL ActivityItem.tsx features (CLEANED UP)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import ReactAudioPlayer from 'react-audio-player';
import { 
  Pencil, MessageSquare, CheckSquare, Eye,
  Clock, ListPlus, Bot, CheckCircle2,
  MessageCircle, Send, Check, CheckCheck, 
  Calendar, CalendarPlus, UserCheck, Bell, 
  Tags, AlertCircle, Phone, PhoneIncoming,
  PhoneOutgoing, PhoneOff, ArrowRight, 
  MoveRight, Copy, FileText, Reply,
  Component, Volume2, VolumeX
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { getActivityMeta } from '../leads/activitytab/activityConstants';
import { useSelector } from 'react-redux';
import './DashboardActivityItem.css';
import logger from '../../utils/logger';

// Console logging for debugging
logger.log('🎨 DashboardActivityItem component loaded with ALL features');

interface DashboardActivityItemProps {
  activity: any;
  handleEditModal: (name: string, id: string, action: string) => void;
  name: string;
}

const DashboardActivityItem: React.FC<DashboardActivityItemProps> = React.memo(({ 
  activity, 
  handleEditModal,
  name 
}) => {
  logger.log('🎨 Rendering activity item with ALL features:', activity);

  // Render count for debugging
  const renderCount = useRef(0);

  // Access Redux store to get bucketUrl for audio files
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const bucketUrl = StoreData?.userData?.userDetails?.bucketUrl;

  // 🆕 CLEANED UP: Get specific icon based on activity type and status - EXACT COPY from ActivityItem.tsx
  const getSpecificIcon = (type: string, details?: any) => {
    interface IconWithMeta {
      icon: LucideIcon;
      iconColor?: string;
      bgColor?: string;
    }

    // Handle WhatsApp message states - EXACT COPY from ActivityItem.tsx
    if (type.includes('whatsapp')) {
      if (type.includes('failed')) return { 
        icon: AlertCircle,
        bgColor: '#EF4444'
      };
      if (type.includes('sending')) return { 
        icon: Send,
        bgColor: '#22C55E'
      };
      if (type.includes('sent')) return { 
        icon: Check,
        bgColor: '#22C55E'
      };
      if (type.includes('delivered')) return { 
        icon: CheckCheck,
        bgColor: '#22C55E'
      };
      if (type.includes('read')) return {
        icon: MessageCircle,
        bgColor: '#22C55E',
        iconColor: '#FFFFFF'
      };
      // Default WhatsApp if no specific state
      return {
        icon: MessageCircle,
        bgColor: '#25D366',
        iconColor: '#FFFFFF'
      };
    }

    // Handle calls with better icons - EXACT COPY from ActivityItem.tsx
    if (type === 'OUTGOING') return {
      icon: PhoneOutgoing,
      bgColor: '#3B82F6'
    };
    if (type === 'INCOMING') return {
      icon: PhoneIncoming,
      bgColor: '#10B981'
    };
    if (type === 'MISSED') return {
      icon: PhoneOff,
      bgColor: '#EF4444'
    };
    
    // Handle general call types
    if (type.includes('call')) {
      if (type.includes('missed')) return { icon: PhoneOff, bgColor: '#EF4444' };
      if (type.includes('incoming')) return { icon: PhoneIncoming, bgColor: '#10B981' };
      if (type.includes('outgoing')) return { icon: PhoneOutgoing, bgColor: '#3B82F6' };
      return { icon: Phone, bgColor: '#0284C7' };
    }
    
    // Handle status and list updates - EXACT COPY from ActivityItem.tsx
    if (type === 'status_updated' || type.includes('Status updated')) return {
      icon: ArrowRight,
      bgColor: '#F59E0B'
    };
    if (type.includes('lead_moved') || type.includes('moved to')) return {
      icon: MoveRight,
      bgColor: '#6366F1'
    };

    // Handle different lead states
    if (type === 'lead_moved') return { icon: MoveRight, bgColor: '#6366F1' };
    if (type === 'lead_assigned') return { icon: UserCheck, bgColor: '#3B82F6' };
    if (type === 'lead_copied') return { icon: Copy, bgColor: '#6366F1' };

    // Handle task states - EXACT COPY from ActivityItem.tsx
    if (type === 'task_created' || type.includes('follow-up task created')) return {
      icon: CalendarPlus,
      bgColor: '#8B5CF6'
    };
    if (type === 'task_completed') return {
      icon: CheckCircle2,
      bgColor: '#10B981'
    };
    if (type === 'due_date_updated') return {
      icon: Calendar,
      bgColor: '#8B5CF6'
    };
    if (type === 'task_note_added') return { 
      icon: MessageSquare, 
      bgColor: '#8B5CF6' 
    };
    if (type === 'follow_up_task') return { 
      icon: Bell, 
      bgColor: '#F59E0B' 
    };

    // Handle automation and tasks
    if (type.includes('automation')) return { 
      icon: Bot, 
      bgColor: '#8B5CF6' 
    };

    // Handle notes and messages - EXACT COPY from ActivityItem.tsx
    if (type === 'note_created') return {
      icon: FileText,
      bgColor: '#6366F1'
    };
    if (type.includes('replied')) return {
      icon: Reply,
      bgColor: '#3B82F6'
    };
    
    // Default fallback for unhandled types
    return {
      icon: Clock,
      bgColor: '#64748B'
    };
  };

  // 🆕 COPIED FROM ActivityItem.tsx - Get metadata with custom icon handling
  const iconMeta = getSpecificIcon(activity.type);
  const { bgColor } = getActivityMeta(activity.type);
  const Icon = iconMeta.icon;
  
  // 🆕 COPIED FROM ActivityItem.tsx - Format time for display
  const time = useMemo(() => {
    const utcDate = moment.utc(activity.performedAt || activity.createdAt);
    const istDate = utcDate.utcOffset(330);
    const currentMoment = moment().utcOffset(330);
    
    if (istDate.isSame(currentMoment, 'day')) {
      return `Today ${istDate.format('hh:mm a')}`;
    } else if (istDate.isSame(currentMoment.clone().subtract(1, 'day'), 'day')) {
      return `Yesterday ${istDate.format('hh:mm a')}`;
    } else if (istDate.isSame(currentMoment, 'year')) {
      return istDate.format('DD MMM, hh:mm a');
    } else {
      return istDate.format('DD MMM YYYY, hh:mm a');
    }
  }, [activity.performedAt, activity.createdAt]);

  // 🆕 COPIED FROM ActivityItem.tsx - Get creator name
  const creatorName = useMemo(() => {
    if (!activity.createdBy) return '';
    if (typeof activity.createdBy === 'string') return activity.createdBy;
    return `${activity.createdBy.firstName || ''} ${activity.createdBy.lastName || ''}`.trim();
  }, [activity.createdBy]);

  // 🆕 COPIED FROM ActivityItem.tsx - Get activity background
  const getActivityBackground = (type: string) => {
    switch (type) {
      case 'task_created':
      case 'task_completed':
      case 'automation':
      case 'task_note_added':
        return 'bg-activity-purple';
      case 'lead_moved':
      case 'lead_copied':
      case 'status_updated':
        return 'bg-activity-blue';
      default:
        return '';
    }
  };

  // 🆕 COPIED FROM ActivityItem.tsx - Check if activity has audio files (call recordings)
  const getAudioFiles = () => {
    if (!activity.files || !Array.isArray(activity.files)) return [];
    
    return activity.files.filter((file: any) => {
      const fileName = file.fileName || file.filePath || '';
      const audioExtensions = ['.mp3', '.wav', '.m4a', '.mpga', '.ogg', '.webm'];
      return audioExtensions.some(ext => fileName.toLowerCase().includes(ext));
    });
  };

  const audioFiles = getAudioFiles();

  // 🆕 COPIED FROM ActivityItem.tsx - Render call recording audio player
  const renderCallRecordings = () => {
    if (audioFiles.length === 0) return null;

    return (
      <div className="call-recordings-container">
        {audioFiles.map((audioFile: any, index: number) => (
          <div key={index} className="audio-container">
            <div className="audio-header">
              <Volume2 size={14} className="audio-icon" />
              <span className="audio-label">Call Recording</span>
              <span className="audio-duration">
                {audioFile.fileSize || 'Unknown size'}
              </span>
            </div>
            <ReactAudioPlayer
              src={`${bucketUrl}${audioFile.filePath}`}
              controls
              className="lead-audio-player"
              preload="metadata"
              title={audioFile.fileName || 'Call Recording'}
            />
            <div className="audio-meta">
              <span className="audio-filename">{audioFile.fileName}</span>
              {audioFile.uploadedAt && (
                <span className="audio-timestamp">
                  {moment(audioFile.uploadedAt).format('DD MMM, hh:mm a')}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Get lead name - for dashboard context
  const getLeadName = () => {
    return activity.lead?.name || 
           activity.leadName || 
           activity.leadDetails?.name ||
           'Unknown Lead';
  };

  // Increment render count for debugging
  useEffect(() => {
    renderCount.current += 1;
    logger.log(`🔄 ActivityItem render count: ${renderCount.current}`, {
      activityId: activity._id,
      type: activity.type,
      leadName: getLeadName(),
      iconUsed: Icon.name || 'Unknown',
      bgColor: iconMeta.bgColor || bgColor
    });
  });

  return (
    <div className={`dashboard-activity-item ${getActivityBackground(activity.type)}`} data-activity-type={activity.type}>
      {/* Lead Name Header - for dashboard */}
      <div className="lead-name-header">
        <span className="lead-name">{getLeadName()}</span>
      </div>

      <div className="activity-line"></div>
      
      {/* 🆕 COPIED FROM ActivityItem.tsx - Main layout with icon */}
      <div className="activity-main-layout">
        <div 
          className="activity-icon" 
          style={{ 
            backgroundColor: iconMeta.bgColor || bgColor,
            color: iconMeta.iconColor || '#FFFFFF'
          }}
          data-type={activity.type}
        >
          <Icon 
            size={24} 
            strokeWidth={activity.type.includes('whatsapp') ? 2.5 : 2}
          />
        </div>

        <div className="activity-content">
          {/* 🆕 COPIED FROM ActivityItem.tsx - Activity header (edit button removed per user) */}
          <div className="activity-header">
            <div className="activity-time">{time}</div>
            {/* Edit button removed per user request */}
          </div>

          <div className="activity-description">
            <div className="activity-label">{activity.label}</div>
            
            {/* 🆕 COPIED FROM ActivityItem.tsx - Notes content */}
            {activity.notes && (
              <div className="note-content">
                {activity.notes}
              </div>
            )}

            {/* 🆕 COPIED FROM ActivityItem.tsx - Render call recordings */}
            {renderCallRecordings()}

            {/* 🆕 COPIED FROM ActivityItem.tsx - Extra details with call duration */}
            {activity.extraDetails && (
              <div className="extra-details">
                {/* Add call duration handling for OUTGOING and INCOMING */}
                {['OUTGOING', 'INCOMING'].includes(activity.type) && (
                  <span className="call-duration">
                    {(!activity.extraDetails?.duration || 
                      activity.extraDetails?.duration === "0" || 
                      activity.extraDetails?.duration === null) 
                      ? "Call not picked" 
                      : `Duration: ${parseInt(activity.extraDetails.duration)} seconds`}
                  </span>
                )}
                
                {activity.extraDetails.templateName && (
                  <span className="template-info">
                    Template: {activity.extraDetails.templateName}
                  </span>
                )}
                {activity.extraDetails.campaignName && (
                  <span className="campaign-info">
                    Campaign: {activity.extraDetails.campaignName}
                  </span>
                )}
              </div>
            )}

            {/* 🆕 COPIED FROM ActivityItem.tsx - Task due date */}
            {activity.task?.toBePerformAt && (
              <div className="due-date">
                Due: {moment(activity.task.toBePerformAt).format('DD MMM, hh:mm a')}
              </div>
            )}
          </div>

          {/* 🆕 COPIED FROM ActivityItem.tsx - Creator info */}
          {creatorName && (
            <div className="creator-info">
              <div className="creator-avatar">
                {creatorName.charAt(0)}
              </div>
              <span className="creator-name">{creatorName}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

DashboardActivityItem.displayName = 'DashboardActivityItem';

export default DashboardActivityItem;